import React, { useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaUsers } from 'react-icons/fa';
import { showSuccess, showError, showInfo } from '../utils/toast';

const API_ADD = 'https://dev.sliitleo.org/Backend/addregistration.php';
const API_INSTALLATION = 'https://dev.sliitleo.org/Backend/installation_reg.php';

export default function AddRegistration() {
  const user = useMemo(() => {
    return JSON.parse(localStorage.getItem('user')) || {
      SLIITID: '',
      name: 'Unknown User',
      role: 'Member',
      image: '/user.png',
    };
  }, []);

  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    sliitId: '',
    name: '',
    faculty: '',
    nic: '',
    mobile: '',
    email: '',
    prevCeremony: '',
    attending: '',
    food: '',
    year: '',
    amount: '',
    Verify: 1,
    VerifiedBy: user.SLIITID || '',
    VerifiedDate: new Date().toISOString().slice(0, 16), // yyyy-MM-ddTHH:mm
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleRadio = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  async function readJsonSafe(res) {
    const txt = await res.text();
    try { return { json: JSON.parse(txt), raw: txt, ok: res.ok, status: res.status }; }
    catch { return { json: null, raw: txt, ok: res.ok, status: res.status }; }
  }

  async function sendVerifiedEmail({ regid }) {
    if (!regid) {
      showInfo('Saved without email: could not detect RegID from server response.');
      return;
    }

    const params = new URLSearchParams();
    params.set('action', 'verify');
    params.set('RegID', String(regid));
    params.set('Verify', '1');
    params.set('VerifiedBy', String(user.SLIITID || ''));
    params.set('VerifiedByName', String(user.name || 'Admin'));
    params.set('SendEmail', '1');     // email on verify only
    params.set('EmailType', 'verified');
    params.set('Debug', '1');

    const res = await fetch(API_INSTALLATION, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    const { json, raw, ok, status } = await readJsonSafe(res);

    if (!ok || !json?.success) {
      const msg = json?.message || json?.error || `HTTP ${status}`;
      showError('Saved, but email failed: ' + msg);
      console.warn('Email verify POST failed:', { status, raw, json });
      return;
    }

    if (json.email_sent === true) {
      showSuccess('Registration saved & verification email sent ✅');
    } else if (json.email_error) {
      showInfo('Registration saved. Email could not be sent: ' + json.email_error);
    } else {
      showSuccess('Registration saved & marked verified.');
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Validate required
    if (!form.sliitId || !form.name || !form.email || !form.amount) {
      showError('Please fill in SLIIT ID, Name, Email, and Amount.');
      return;
    }

    // Coerce numbers
    const amountNum = parseFloat(String(form.amount).replace(/[^\d.]/g, ''));
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      showError('Please enter a valid amount.');
      return;
    }
    const yearNum = form.year ? Number(form.year) : null;

    // Build payload with correct types
    const payload = {
      InstallationID: 1,                            // INT
      Email: form.email || null,
      SLIITID: form.sliitId || null,
      Name: form.name || null,
      NIC: form.nic || null,
      Contact: form.mobile || null,
      Faculty: form.faculty || null,
      Year: yearNum,                                // INT or null
      FoodPref: form.food || null,
      PrevStatus: form.prevCeremony || null,
      PartStatus: form.attending || null,

      FileURL: null,
      Amount: amountNum,                            // DECIMAL/INT
      Bank: 'Cash-in-hand',
      PaymenMethod: 'Cash',                         // keep key spelling as backend expects

      Json: null,

      Verify: 1,                                    // INT
      VerifiedBy: user.SLIITID || null,            // likely string/ID
      CheckIn: 0,                                   // INT
      CheckedBy: null,

      RegDate: new Date().toISOString().slice(0, 10), // 'YYYY-MM-DD'
      VerifiedDate: ((form.VerifiedDate || new Date().toISOString().slice(0, 16)).replace('T', ':') + ':00')
        .replace('::', ':')                         // 'YYYY-MM-DD HH:MM:SS'

      ,ReportReason: null,
      ReportedBy: null,
      ReportedDate: null,
      CheckedDate: null,

      // ✅ FIX: send INT, not string
      respondFrom: 2,                               // 1 = Google Form, 2 = Web Form (choose any mapping)

      paymentId: null,
      transactionId: null,
      merchId: null,
    };

    try {
      setSubmitting(true);

      const res = await fetch(API_ADD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const { json, raw, ok, status } = await readJsonSafe(res);

      if (!ok || !json) {
        throw new Error(`HTTP ${status}: ${raw?.slice?.(0, 200) || 'Invalid response'}`);
      }
      if (!json.success) {
        // bubble server-side reason (like column mismatch) to the UI
        throw new Error(json.message || 'Backend failed to create registration');
      }

      // best-effort: read new RegID from various shapes
      const regid =
        json.regid ??
        json.RegID ??
        json.id ??
        (json.data && (json.data.regid || json.data.RegID || json.data.id)) ??
        null;

      await sendVerifiedEmail({ regid });

      // reset form
      setForm((prev) => ({
        ...prev,
        sliitId: '',
        name: '',
        faculty: '',
        nic: '',
        mobile: '',
        email: '',
        prevCeremony: '',
        attending: '',
        food: '',
        year: '',
        amount: '',
        Verify: 1,
        VerifiedBy: user.SLIITID || '',
        VerifiedDate: new Date().toISOString().slice(0, 16),
      }));
    } catch (err) {
      console.error('AddRegistration submit failed:', err);
      showError('Error submitting registration: ' + (err?.message || String(err)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex bg-[#111827] text-white min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Topbar user={user} />

        <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 py-6 sm:py-8">
          {/* Header */}
          <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-4 sm:p-5 mb-6 sm:mb-8 flex items-center w-full lg:w-10/12 gap-3 sm:gap-4 shadow-lg">
            <span className="flex items-center gap-2 sm:gap-3">
              <span className="bg-indigo-600 p-1.5 sm:p-2 rounded-full shadow-lg flex items-center justify-center">
                <FaUsers className="text-white text-xl sm:text-2xl" />
              </span>
              <span className="text-base sm:text-lg font-bold tracking-wide text-white drop-shadow">
                Installation <span className="text-gray-400 font-normal hidden sm:inline">/ Add Registration</span>
                <span className="sm:hidden text-gray-400 font-normal">/ Add</span>
              </span>
            </span>
          </div>

          <form onSubmit={handleSubmit} className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-3 sm:p-5 mb-6 sm:mb-8 w-full lg:w-10/12 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
              {/* Inputs... (unchanged visuals) */}
              {/* SLIIT ID */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">SLIIT ID</label>
                <input
                  name="sliitId"
                  value={form.sliitId}
                  onChange={handleChange}
                  type="text"
                  placeholder="Enter SLIIT ID"
                  className="w-full bg-[#23272f] rounded-md px-3 py-2 text-xs sm:text-sm text-white placeholder-gray-400 outline-none"
                  autoComplete="off"
                  required
                />
              </div>

              {/* Member Name */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Member Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  type="text"
                  placeholder="Full Name"
                  className="w-full bg-[#23272f] rounded-md px-3 py-2 text-xs sm:text-sm text-white placeholder-gray-400 outline-none"
                  required
                />
              </div>

              {/* Faculty */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Faculty</label>
                <select
                  name="faculty"
                  value={form.faculty}
                  onChange={handleChange}
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white outline-none"
                >
                  <option value="">Select Faculty</option>
                  <option value="Business Management">Business Management</option>
                  <option value="Humanities & Sciences">Humanities & Sciences</option>
                  <option value="Computing">Computing</option>
                  <option value="Engineering">Engineering</option>
                  <option value="School of Architecture">School of Architecture</option>
                </select>
              </div>

              {/* NIC */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">NIC</label>
                <input
                  name="nic"
                  value={form.nic}
                  onChange={handleChange}
                  type="text"
                  placeholder="Enter NIC"
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white placeholder-gray-400 outline-none"
                />
              </div>

              {/* Mobile */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Mobile Number</label>
                <input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  type="text"
                  placeholder="Mobile number"
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white placeholder-gray-400 outline-none"
                />
              </div>

              {/* Email */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Email</label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  placeholder="Email address"
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white placeholder-gray-400 outline-none"
                  required
                />
              </div>

              {/* Prev Ceremony */}
              <div className="md:col-span-3 space-y-1">
                <label className="block text-xs font-semibold mb-1">Attended Club Installation before?</label>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-1">
                  <label className="flex items-center gap-2 text-xs sm:text-sm">
                    <input type="radio" name="prevCeremony" value="Yes" checked={form.prevCeremony === 'Yes'} onChange={handleRadio} className="accent-indigo-600" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 text-xs sm:text-sm">
                    <input type="radio" name="prevCeremony" value="No" checked={form.prevCeremony === 'No'} onChange={handleRadio} className="accent-indigo-600" />
                    No
                  </label>
                </div>
              </div>

              {/* Attending */}
              <div className="md:col-span-3 space-y-1">
                <label className="block text-xs font-semibold mb-1">Attending the Ceremony?</label>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-1">
                  <label className="flex items-center gap-2 text-xs sm:text-sm">
                    <input type="radio" name="attending" value="Yes" checked={form.attending === 'Yes'} onChange={handleRadio} className="accent-indigo-600" />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 text-xs sm:text-sm">
                    <input type="radio" name="attending" value="No" checked={form.attending === 'No'} onChange={handleRadio} className="accent-indigo-600" />
                    No
                  </label>
                </div>
              </div>

              {/* Year */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Year</label>
                <select
                  name="year"
                  value={form.year}
                  onChange={handleChange}
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white outline-none"
                >
                  <option value="">Select Year</option>
                  <option value="1">1st Year</option>
                  <option value="2">2nd Year</option>
                  <option value="3">3rd Year</option>
                  <option value="4">4th Year</option>
                </select>
              </div>

              {/* Food */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Food Preference</label>
                <select
                  name="food"
                  value={form.food}
                  onChange={handleChange}
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white outline-none"
                >
                  <option value="">Food Preference</option>
                  <option value="Veg">Vegetarian</option>
                  <option value="Non-Veg">Non-Vegetarian</option>
                </select>
              </div>

              {/* Amount */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Amount</label>
                <input
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  type="text"
                  placeholder="Enter amount"
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white placeholder-gray-400 outline-none"
                  required
                />
              </div>

              {/* Verified By */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Verified By</label>
                <select
                  name="VerifiedBy"
                  value={form.VerifiedBy}
                  onChange={handleChange}
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white outline-none mb-3"
                >
                  <option value={user.SLIITID || ''}>{user.name}</option>
                </select>
              </div>

              {/* Verified Date */}
              <div className="space-y-1">
                <label className="block text-xs font-semibold mb-1">Verified Date</label>
                <input
                  type="datetime-local"
                  name="VerifiedDate"
                  value={form.VerifiedDate}
                  onChange={handleChange}
                  className="w-full bg-gray-800 rounded-md px-3 py-2 text-xs sm:text-sm text-white outline-none"
                />
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={submitting}
                className={
                  'w-full max-w-xs mx-auto text-white font-bold py-2.5 px-6 rounded-lg transition-all text-sm sm:text-base shadow-lg flex items-center justify-center gap-2 ' +
                  (submitting ? 'bg-gray-600 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-500/20')
                }
              >
                {submitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                    </svg>
                    Submitting…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit Registration
                  </>
                )}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
